import { useCallback } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../presentation/hooks/store-hooks";
import {
  IExtracao,
  IListaDeduplicacao,
  IProjeto,
  IProjetoTable,
  IRecorteExtracao,
} from "../../../application/models";
import {
  resetExtracaoAbertaState,
  resetExtracaoListFiltrosClienteState,
  resetExtracaoListFiltrosIdUsuarioState,
  resetExtracaoListFiltrosInclusaoEndState,
  resetExtracaoListFiltrosInclusaoStartState,
  resetExtracaoListFiltrosNomeState,
  resetExtracaoListFiltrosNomeUsuarioState,
  resetExtracaoListFiltrosState,
  resetExtracaoListFiltrosUsuarioState,
  setExtracaoAbertaState,
  setExtracaoListFiltrosClienteState,
  setExtracaoListFiltrosIdUsuarioState,
  setExtracaoListFiltrosInclusaoEndState,
  setExtracaoListFiltrosInclusaoStartState,
  setExtracaoListFiltrosNomeState,
  setExtracaoListFiltrosNomeUsuarioState,
  setExtracaoListFiltrosUsuarioState,
  setExtracoesPaginationState,
  setExtracoesState,
  setExtracoesTotalState,
  setProjetoAbertoState,
  setProjetosState,
  setRecortesExtracaoState,
  setListasDeduplicacaoState,
} from "../../../application/states/geo-veles";
import { useExtracaoApi } from "../../../infra/geo-veles";
import { useUIService } from "../../ui";
import { useNavigationService } from "../../navigation";
import { useLayoutExtracaoService } from "../layout-extracao";

export const useExtracoesService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const {
    extracoes,
    extracoesTotal,
    extracaoListFiltros,
    extracoesPagination,
    extracaoAberta,
  } = useAppSelector((state) => state.extracoes);
  const { layoutSelecionado, extensaoArquivoSelecionada } = useAppSelector(
    (state) => state.layoutExtracao
  );

  const { currentUser } = useAppSelector((state) => state.user);
  // API
  const {
    findExtracaoListApi,
    downloadExtracaoApi,
    findProjetobyIdVelesGeoExtracaoApi,
    findProjetosbyIdVelesGeoExtracaoApi,
    getVerifyDuplicateCPFsApi,
  } = useExtracaoApi();
  // Service
  const { setLoading, setAlert } = useUIService();
  const { navigateTo } = useNavigationService();
  const {
    resetLayoutSelecionado,
    resetLayouts,
    resetExtensaoArquivoSelecionada,
  } = useLayoutExtracaoService();

  const setExtracaoFindProjeto = useCallback(
    async (idVelesGeoExtracao: number) => {
      const loadingId = Math.random().toString();
      try {
        setLoading({
          id: loadingId,
          loading: true,
          message: "Buscando projetos associados...",
        });

        const setExtracaoApi = await findProjetobyIdVelesGeoExtracaoApi({
          idVelesGeoExtracao: idVelesGeoExtracao,
        });

        if (setExtracaoApi) {
          dispatch(setProjetoAbertoState(setExtracaoApi));
          return setExtracaoApi;
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
      return null;
    },
    [dispatch, findProjetobyIdVelesGeoExtracaoApi, setAlert, setLoading]
  );

  const getExtracoes = useCallback(async () => {
    if (currentUser) {
      const loadingId = Math.random().toString();
      setLoading({
        id: loadingId,
        loading: true,
        message: "Buscando histórico de extrações...",
      });

      try {
        const findExtracoes = await findExtracaoListApi({
          filter: {
            idUsuario: currentUser.IdUsuario,
            idVelesPerfilInterno: currentUser.IdVelesPerfilInterno,
            inclusaoMax: extracaoListFiltros.inclusaoEnd,
            inclusaoMin: extracaoListFiltros.inclusaoStart,
            nome: extracaoListFiltros.nome,
            cliente: extracaoListFiltros.cliente,
            usuario: extracaoListFiltros.usuario,
            nomeUsuario: extracaoListFiltros.nomeUsuario,
          },
          pagination: {
            page: extracoesPagination.page,
            pageSize: extracoesPagination.limit,
          },
        });

        dispatch(
          setExtracoesState({
            extracoes: findExtracoes.extracoes,
            total: findExtracoes.total,
          })
        );

      } catch (error: any) {
        setAlert({
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Erro desconhecido",
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
    }
  }, [
    currentUser,
    dispatch,
    extracaoListFiltros.cliente,
    extracaoListFiltros.inclusaoEnd,
    extracaoListFiltros.inclusaoStart,
    extracaoListFiltros.nome,
    extracaoListFiltros.nomeUsuario,
    extracaoListFiltros.usuario,
    extracoesPagination.limit,
    extracoesPagination.page,
    findExtracaoListApi,
    setAlert,
    setLoading,
  ]);

  const getExtracoesFindProjetos = useCallback(async () => {
    
    try {
      const idExtracoes: number[] = [];
      extracoes.forEach((extracao) => {
        idExtracoes.push(extracao.extracao.IdVelesGeoExtracao);
      });

      const findProjetosbyIdVelesGeoExtracao =
        await findProjetosbyIdVelesGeoExtracaoApi({
          idVelesGeoExtracao: idExtracoes,
        });
      if (findProjetosbyIdVelesGeoExtracao) {
        const projetosTable: IProjetoTable[] = [];
        for (
          let i: number = 0;
          i < findProjetosbyIdVelesGeoExtracao.length;
          i++
        ) {
          const projeto: IProjeto | null = findProjetosbyIdVelesGeoExtracao[i];
          if (projeto) {
            projetosTable.push({
              ...projeto,
              NomeUsuario: currentUser?.Nome ?? "",
              LoginUsuario: currentUser?.LoginUsuario ?? "",
            });
          }
        }

        dispatch(
          setProjetosState({
            projetos: [...projetosTable],
            total: 0,
          })
        );
      }
    } catch (projetoError) {
    }
  }, [
    currentUser?.LoginUsuario,
    currentUser?.Nome,
    dispatch,
    extracoes,
    findProjetosbyIdVelesGeoExtracaoApi,
  ]);

  const getRecortes = useCallback(async () => {
    try {
      let recortesExtracao: IRecorteExtracao[] = [];
      for (let i = 0; i < extracoes.length; i++) {
        const recorteExtracao: IRecorteExtracao = {
          //Nome: "",
          IdVelesGeoRecorteExtracao: 0,
          IdVelesGeoProjeto: extracoes[i].extracao.IdVelesGeoProjeto,
          Recortes: extracoes[i].extracao.Recortes ?? [],
          Inclusao: extracoes[i].extracao.Inclusao,
          Atualizacao: extracoes[i].extracao.Atualizacao,
        };

        if (recorteExtracao.Recortes !== null) {
          recortesExtracao.push(recorteExtracao);
        }
      }
      dispatch(setRecortesExtracaoState(recortesExtracao));
    } catch (error) {}
  }, [dispatch, extracoes]);

  const setExtracaoAberta = useCallback(
    (extracao: IExtracao) => {
      dispatch(setExtracaoAbertaState(extracao));
      navigateTo({
        route: "/geo-veles/extracoes/download",
      });
    },
    [dispatch, navigateTo]
  );

  const resetExtracaoAberta = useCallback(() => {
    dispatch(resetExtracaoAbertaState());
  }, [dispatch]);

  const setExtracoesTotal = useCallback(
    (total: number) => {
      dispatch(setExtracoesTotalState(total));
    },
    [dispatch]
  );

  const setExtracoesPagination = useCallback(
    (data: { page: number; limit: number }) => {
      dispatch(
        setExtracoesPaginationState({
          page: data.page,
          limit: data.limit,
        })
      );
    },
    [dispatch]
  );

  const setExtracaoListFiltrosNome = useCallback(
    (nome: string) => {
      dispatch(setExtracaoListFiltrosNomeState(nome));
    },
    [dispatch]
  );

  const resetExtracaoListFiltrosNome = useCallback(() => {
    dispatch(resetExtracaoListFiltrosNomeState());
  }, [dispatch]);

  const setExtracaoListFiltrosInclusaoStart = useCallback(
    (dataInclusao: Date) => {
      dispatch(setExtracaoListFiltrosInclusaoStartState(dataInclusao));
    },
    [dispatch]
  );

  const resetExtracaoListFiltrosInclusaoStart = useCallback(() => {
    dispatch(resetExtracaoListFiltrosInclusaoStartState());
  }, [dispatch]);

  const setExtracaoListFiltrosInclusaoEnd = useCallback(
    (dataInclusao: Date) => {
      dispatch(setExtracaoListFiltrosInclusaoEndState(dataInclusao));
    },
    [dispatch]
  );

  const resetExtracaoListFiltrosInclusaoEnd = useCallback(() => {
    dispatch(resetExtracaoListFiltrosInclusaoEndState());
  }, [dispatch]);

  const setExtracaoListFiltrosIdUsuario = useCallback(
    (idUsuario: number) => {
      dispatch(setExtracaoListFiltrosIdUsuarioState(idUsuario));
    },
    [dispatch]
  );

  const resetExtracaoListFiltrosIdUsuario = useCallback(() => {
    dispatch(resetExtracaoListFiltrosIdUsuarioState());
  }, [dispatch]);

  const resetExtracaoListFiltros = useCallback(() => {
    dispatch(resetExtracaoListFiltrosState());
  }, [dispatch]);

  const setExtracaoListFiltrosUsuario = useCallback(
    (usuario: string) => {
      dispatch(setExtracaoListFiltrosUsuarioState(usuario));
    },
    [dispatch]
  );

  const resetExtracaoListFiltrosUsuario = useCallback(() => {
    dispatch(resetExtracaoListFiltrosUsuarioState());
  }, [dispatch]);

  const setExtracaoListFiltrosNomeUsuario = useCallback(
    (usuario: string) => {
      dispatch(setExtracaoListFiltrosNomeUsuarioState(usuario));
    },
    [dispatch]
  );

  const resetExtracaoListFiltrosNomeUsuario = useCallback(() => {
    dispatch(resetExtracaoListFiltrosNomeUsuarioState());
  }, [dispatch]);

  const setExtracaoListFiltrosCliente = useCallback(
    (cliente: string) => {
      dispatch(setExtracaoListFiltrosClienteState(cliente));
    },
    [dispatch]
  );

  const resetExtracaoListFiltrosCliente = useCallback(() => {
    dispatch(resetExtracaoListFiltrosClienteState());
  }, [dispatch]);

  const downloadExtracao = useCallback(async () => {
    if (currentUser) {
      if (extracaoAberta) {
        if (layoutSelecionado) {
          if (extensaoArquivoSelecionada) {
            const loadingId = Math.random().toString();
            try {
              setLoading({
                id: loadingId,
                loading: true,
                message: "Aguarde enquanto o arquivo é gerado...",
              });

              await downloadExtracaoApi({
                IdVelesGeoExtracao: extracaoAberta.IdVelesGeoExtracao,
                IdUsuario: currentUser.IdUsuario,
                IdVelesGeoLayoutExtracao:
                  layoutSelecionado.IdVelesGeoLayoutExtracao,
                fileType: extensaoArquivoSelecionada.value,
                Nome: extracaoAberta.Nome,
              });
              setAlert({
                message: "Arquivo gerado com sucesso!",
                type: "success",
              });
              navigateTo({
                route: "/geo-veles/extracoes",
              });
              resetLayoutSelecionado();
              resetLayouts();
              resetExtensaoArquivoSelecionada();
            } catch (error: any) {
              setAlert({
                message:
                  error.response.data.error || error.response.data.message,
                type: "error",
              });
            } finally {
              setLoading({
                id: loadingId,
                loading: false,
              });
            }
          }
        }
      }
    }
  }, [
    currentUser,
    downloadExtracaoApi,
    extensaoArquivoSelecionada,
    extracaoAberta,
    layoutSelecionado,
    navigateTo,
    resetExtensaoArquivoSelecionada,
    resetLayoutSelecionado,
    resetLayouts,
    setAlert,
    setLoading,
  ]);

  const setVefificarCpfsDuplicados = useCallback(
    async (IdExtracao: number, listasParaAnalise: IListaDeduplicacao[]) => {
      const loadingId = Math.random().toString();
      setLoading({
        id: loadingId,
        loading: true,
        message: "Verificando CPFs duplicados...",
      });

      try {
        const getVerificarCpfsDuplicados = await getVerifyDuplicateCPFsApi({
          idVelesGeoExtracao: IdExtracao,
          idUsuario: currentUser?.IdUsuario ?? 0,
          cpfList: listasParaAnalise,
        });

        if (
          !getVerificarCpfsDuplicados ||
          !Array.isArray(getVerificarCpfsDuplicados)
        ) {
          throw new Error(
            "Resposta da API inválida. Esperado um array de CPFs duplicados."
          );
        } else {
          if (getVerificarCpfsDuplicados.length > 0) {
            const cpfsDuplicados: string[] = getVerificarCpfsDuplicados.map(
              (cpf) => cpf.replace(/\D/g, "")
            );
            const listasAtualizadas: IListaDeduplicacao[] =
              listasParaAnalise.map((lista) => {
                const cpfsNormalizados =
                  lista.CPFs?.map((cpf) => cpf.replace(/\D/g, "")) || [];

                const cpfsNaoDuplicados = cpfsNormalizados.filter(
                  (cpf) => !cpfsDuplicados.includes(cpf)
                );

                const cpfsDuplicadosNaLista = cpfsNormalizados.filter((cpf) =>
                  cpfsDuplicados.includes(cpf)
                );

                const novaOrdem = [
                  ...cpfsDuplicadosNaLista,
                  ...cpfsNaoDuplicados,
                ];

                return {
                  ...lista,
                  CPFs: novaOrdem,
                  CPFsDuplicados: cpfsDuplicadosNaLista, // <-- aqui!
                };
              });

            dispatch(
              setListasDeduplicacaoState({
                listasDeduplicacao: listasAtualizadas,
              })
            );

            dispatch(
              setExtracoesState({
                extracoes: extracoes.map((item) =>
                  item.extracao.IdVelesGeoExtracao === IdExtracao
                    ? { ...item, listasDeduplicacao: listasAtualizadas }
                    : item
                ),
                total: extracoesTotal, // Preserva o total atual
              })
            );


            setAlert({
              message: "Existem CPF(s) duplicado(s) na(s) lista(s).",
              type: "warning",
            });
          } else {
            setAlert({
              message: "Nenhum CPF(s) duplicado(s) na(s) lista(s).",
              type: "success",
            });
            return;
          }
        }
      } catch (error: any) {

        setAlert({
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            "Erro desconhecido ao verificar CPFs duplicados.",
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
    },
    [currentUser?.IdUsuario, dispatch, extracoes, extracoesTotal, getVerifyDuplicateCPFsApi, setAlert, setLoading]
  );

  return {
    getExtracoes,
    setExtracaoAberta,
    resetExtracaoAberta,
    setExtracoesTotal,
    setExtracoesPagination,
    setExtracaoListFiltrosNome,
    resetExtracaoListFiltrosNome,
    setExtracaoListFiltrosInclusaoStart,
    resetExtracaoListFiltrosInclusaoStart,
    setExtracaoListFiltrosInclusaoEnd,
    resetExtracaoListFiltrosInclusaoEnd,
    setExtracaoListFiltrosIdUsuario,
    resetExtracaoListFiltrosIdUsuario,
    resetExtracaoListFiltros,
    downloadExtracao,
    setExtracaoListFiltrosUsuario,
    resetExtracaoListFiltrosUsuario,
    setExtracaoListFiltrosNomeUsuario,
    resetExtracaoListFiltrosNomeUsuario,
    setExtracaoListFiltrosCliente,
    resetExtracaoListFiltrosCliente,
    setExtracaoFindProjeto,
    getExtracoesFindProjetos,
    getRecortes,
    setVefificarCpfsDuplicados,
  };
};
